import type { Color } from '@onefootprint/design-tokens';
import type { Icon } from '@onefootprint/icons';
import { IcoChevronDown16 } from '@onefootprint/icons';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { forwardRef, useState } from 'react';
import Button from '../button';
import type { ButtonSize, ButtonVariant } from '../button/button.types';
import Dropdown from '../dropdown';

export type DropdownButtonOption = {
  label: string;
  value: string;
  onSelect: () => void;
  disabled?: boolean;
  iconLeft?: Icon;
};

export type DropdownButtonProps = {
  children: string;
  options: DropdownButtonOption[];
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  size?: ButtonSize;
  testID?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
};

const chevronColorByVariant: Record<ButtonVariant, Color> = {
  primary: 'quinary',
  secondary: 'primary',
  destructive: 'quinary',
};

/* The chevron has built-in visual padding inside its 16x16 box, so we
   shave 2px off the right to balance against the left edge optically. */
const paddingRightBySize: Record<ButtonSize, string> = {
  large: '22px',
  default: '10px',
  compact: '6px',
};

const DropdownButton = forwardRef<HTMLButtonElement, DropdownButtonProps>(
  (
    {
      children,
      className,
      disabled = false,
      loading = false,
      options,
      size = 'default',
      testID,
      type = 'button',
      variant = 'primary',
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    return (
      <Dropdown.Root open={open} onOpenChange={setOpen}>
        <RadixDropdown.Trigger asChild disabled={disabled || loading}>
          <Button
            className={className}
            disabled={disabled}
            iconColor={chevronColorByVariant[variant]}
            loading={loading}
            ref={ref}
            size={size}
            style={{ paddingRight: paddingRightBySize[size] }}
            suffixIcon={IcoChevronDown16}
            testID={testID}
            type={type}
            variant={variant}
          >
            {children}
          </Button>
        </RadixDropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="start" sideOffset={6}>
            <Dropdown.Group>
              {options.map(option => (
                <Dropdown.Item
                  key={option.value}
                  iconLeft={option.iconLeft}
                  disabled={option.disabled}
                  onSelect={option.onSelect}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Group>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    );
  },
);

export default DropdownButton;
